import { TipStatus, WorkerStatus } from "model/Tip";
import type { TipDetails, Worker } from "model/Tip";
import { parseWorkTime } from "utils/utils";
import { isPaidOutTip, isReadyToPayoutTip } from "../logic/tips.v2.logic";
export {
  getWorkerDisplayInitials as getWorkerInitials,
  getWorkerName,
} from "../logic/tips.worker.logic";

export type TipRowPaymentStatus = "bank" | "failed" | "partlyPaid" | "swish";

interface FormatWorkerTimeParams {
  readonly hourLabel: string;
  readonly minuteLabel: string;
  readonly worker: Worker;
}

interface GetPaymentLabelParams {
  readonly fallbackStatus: TipRowPaymentStatus | null;
  readonly status: WorkerStatus | null | undefined;
  readonly t: (key: string) => string;
}

interface GetTipPaymentStatusLabelParams {
  readonly status: TipRowPaymentStatus;
  readonly t: (key: string) => string;
}

interface GetDisplayRemainingAmountParams {
  readonly currentAmount: number;
  readonly hasPersistedDistribution: boolean;
  readonly inputValue: string;
  readonly isReadyForPayout: boolean;
  readonly remainingAmount: number;
  readonly rowPaymentStatus: TipRowPaymentStatus | null;
}

export function getTipRowPaymentStatus(
  tip: TipDetails,
): TipRowPaymentStatus | null {
  if (tip.status === TipStatus.ClosedManually) return "bank";
  if (tip.status === TipStatus.Closed) return "swish";
  if (tip.status !== TipStatus.Done) return null;

  const workers = tip.workers ?? [];
  if (workers.length === 0) return null;

  const failedWorkers = workers.filter(
    (worker) => worker.status === WorkerStatus.Failed,
  ).length;

  if (failedWorkers === 0) return null;
  if (failedWorkers === workers.length) return "failed";
  return "partlyPaid";
}

export function getDisplayRemainingAmount({
  currentAmount,
  hasPersistedDistribution,
  inputValue,
  isReadyForPayout,
  remainingAmount,
  rowPaymentStatus,
}: GetDisplayRemainingAmountParams): number {
  if (
    inputValue.trim().length === 0 ||
    isReadyForPayout ||
    hasPersistedDistribution ||
    rowPaymentStatus !== null
  ) {
    return remainingAmount;
  }

  return currentAmount;
}

export function formatWorkerTime({
  hourLabel,
  minuteLabel,
  worker,
}: FormatWorkerTimeParams): string {
  if (worker.approvedTimeReportCount <= 0) return "";
  const workTime = parseWorkTime(worker.workTime);
  if (workTime.minutes === 0) return `${workTime.hours}${hourLabel}`;
  return `${workTime.hours}${hourLabel} ${workTime.minutes}${minuteLabel}`;
}

export function getPaymentLabel({
  fallbackStatus,
  status,
  t,
}: GetPaymentLabelParams): string {
  switch (status) {
    case WorkerStatus.Paid:
      return t("Tips.PaidOut");
    case WorkerStatus.PaidManually:
      return t("Tips.ManuallyPaid");
    case WorkerStatus.Created:
      return t("Tips.PaymentStarted");
    case WorkerStatus.Failed:
      return t("Tips.PaymentFailed");
    case WorkerStatus.null:
    case null:
    case undefined:
      if (fallbackStatus === "bank") return t("MonthlyTipReport.Bank");
      if (fallbackStatus === "swish") return t("Tips.Swish");
      return t("Tips.NotInitiated");
  }
}

export function getTipPaymentStatusLabel({
  status,
  t,
}: GetTipPaymentStatusLabelParams): string {
  if (status === "bank") return t("MonthlyTipReport.Bank");
  if (status === "swish") return t("Tips.Swish");
  if (status === "partlyPaid") return t("CompanyTip.PartlyPaid");
  return t("Tips.PaymentFailed");
}

export function getTipPaymentStatusTone(
  status: TipRowPaymentStatus,
): "danger" | "success" | "warning" {
  if (status === "partlyPaid") return "warning";
  if (status === "failed") return "danger";
  return "success";
}

export function getTipPaymentStatusIcon(status: TipRowPaymentStatus): string {
  if (status === "partlyPaid" || status === "failed") {
    return "v2/warning-triangle";
  }
  return "v2/checkmark";
}

export function isReadyToSplitTip(tip: TipDetails): boolean {
  return tip.amount > 0 && !isReadyToPayoutTip(tip) && !isPaidOutTip(tip);
}

export function isUnfilledTip(tip: TipDetails): boolean {
  return tip.amount === 0 && !isReadyToPayoutTip(tip) && !isPaidOutTip(tip);
}

export function isActionableReadyToPayoutTip(tip: TipDetails): boolean {
  return isReadyToPayoutTip(tip) && getTipRowPaymentStatus(tip) === null;
}

export function isManualPaidOutTip(tip: TipDetails): boolean {
  return tip.status === TipStatus.ClosedManually;
}
